import { useEffect, useRef } from "react";

/**
 * Adds `.in` class to elements with `.reveal` or `.reveal-zoom` when they enter the viewport.
 * Mount once at the top of any page that uses these classes.
 */
export default function useScrollReveal(deps = []) {
  const observed = useRef(new Set());

  useEffect(() => {
    // Add reveal-init class to HTML to enable hiding non-revealed elements
    document.documentElement.classList.add('reveal-init');
    
    // Fallback: if IntersectionObserver is not available, show all elements immediately
    if (typeof IntersectionObserver === "undefined") {
      const targets = document.querySelectorAll(".reveal, .reveal-zoom");
      targets.forEach((t) => t.classList.add("in"));
      return;
    }

    const targets = document.querySelectorAll(".reveal, .reveal-zoom");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -30px 0px" }
    );
    
    targets.forEach((t) => {
      if (!observed.current.has(t)) {
        // Check if element is already in viewport on mount
        const rect = t.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        
        if (isInViewport) {
          // Element is already visible, add class immediately
          t.classList.add("in");
        } else {
          // Element not visible yet, observe it
          io.observe(t);
        }
        observed.current.add(t);
      }
    });
    
    return () => {
      io.disconnect();
      document.documentElement.classList.remove('reveal-init');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
